import { useState, useEffect, useCallback, useRef } from "react";
import {
  getSemestralParkingService,
  closeCycleService,
  createCycleService,
  getCommerceConfigService,
} from "modules/copropiedades/services/semestrales";
import {
  getCommerceId,
  getCommerceName,
  getUserCommerces,
  switchCommerce,
  initializeUserCommerce,
  getCurrentUser,
  hasModulePermission,
  getUserPermissionsForModule,
} from "modules/copropiedades/services/authService";

// ============================================================
// 🔥 NUEVA FUNCIÓN: Filtrar comercios por permiso en un módulo
// ============================================================
const filterCommercesByModulePermission = (commerces, moduleName) => {
  if (!commerces || !Array.isArray(commerces)) return [];

  const userData = getCurrentUser() || {};
  const permissions = userData.permissions || [];

  return commerces.filter((commerce) => {
    // Buscar si existe un permiso para este comercio en el módulo especificado
    const permiso = permissions.find(
      (p) => p.co_prop_id === commerce.id && p.co_table === moduleName
    );
    // Solo incluir si el permiso existe y "ver" es true
    return permiso && permiso.rol?.ver === true;
  });
};

export const useSemestrales = () => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    uniquePlates: 0,
    activeCount: 0,
    completedCount: 0,
    vehiclesInside: 0,
    vehiclesOutside: 0,
  });
  const [commerceConfig, setCommerceConfig] = useState(null);
  const [terminales, setTerminales] = useState({
    entrada: null,
    salida: null,
    otros: [],
  });
  const [commerces, setCommerces] = useState([]);
  const [currentCommerceId, setCurrentCommerceId] = useState(null);
  const [currentCommerceName, setCurrentCommerceName] = useState("");
  const [hasPermission, setHasPermission] = useState(false);
  const [permisosRegistros, setPermisosRegistros] = useState({
    ver: false,
    entrada: false,
    salida: false,
  });
  const [initialized, setInitialized] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);
  const hasLoaded = useRef(false);

  // ============================================================
  // 1. FUNCIONES AUXILIARES
  // ============================================================

  const loadCommerceConfig = useCallback(async () => {
    try {
      const result = await getCommerceConfigService();
      if (result.success && result.data) {
        setCommerceConfig(result.data);
        setTerminales({
          entrada: result.data.entrada,
          salida: result.data.salida,
          otros: result.data.otrosReaders || [],
        });
        return result.data;
      } else {
        console.error("❌ Error al cargar configuración:", result.error);
        return null;
      }
    } catch (error) {
      console.error("❌ Error al cargar configuración:", error);
      return null;
    }
  }, []);

  const loadCommerces = useCallback(async () => {
    try {
      const result = await getUserCommerces();
      if (result.success) {
        // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "registros"
        const filteredCommerces = filterCommercesByModulePermission(result.commerces, "registros");
        setCommerces(filteredCommerces);
        return filteredCommerces;
      }
      return [];
    } catch (error) {
      console.error("❌ Error al cargar comercios:", error);
      return [];
    }
  }, []);

  // ============================================================
  // 2. FUNCIÓN PARA CARGAR REGISTROS
  // ============================================================

  const loadRecords = useCallback(async () => {
    // Si ya se intentó cargar y no hay permisos, no volver a intentar
    if (loadAttempted && !hasPermission) {
      return;
    }

    // Verificar permisos antes de cargar
    if (!hasPermission) {
      setError("No tiene permisos para ver registros");
      setLoadAttempted(true);
      return;
    }

    if (isLoadingRef.current) return;

    // Asegurar que tenemos commerceId
    let commerceId = getCommerceId();
    if (!commerceId) {
      // Si hay comercios disponibles pero no hay commerceId, usar el primero
      if (commerces.length > 0) {
        const commerce = commerces[0];
        switchCommerce(commerce.id, commerce.name);
        setCurrentCommerceId(commerce.id);
        setCurrentCommerceName(commerce.name);
        commerceId = commerce.id;
      } else {
        // Intentar inicializar
        const result = await initializeUserCommerce();
        if (result.success) {
          // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "registros"
          const filteredCommerces = filterCommercesByModulePermission(
            result.commerces,
            "registros"
          );

          let finalCommerceId = result.commerceId;
          let finalCommerceName = result.commerceName;

          if (
            !filteredCommerces.some((c) => c.id === finalCommerceId) &&
            filteredCommerces.length > 0
          ) {
            finalCommerceId = filteredCommerces[0].id;
            finalCommerceName = filteredCommerces[0].name;
            switchCommerce(finalCommerceId, finalCommerceName);
          }

          commerceId = finalCommerceId;
          setCurrentCommerceId(finalCommerceId);
          setCurrentCommerceName(finalCommerceName || "");
          setCommerces(filteredCommerces);
        } else {
          setError("No se pudo obtener el comercio asociado al usuario");
          setLoadAttempted(true);
          return;
        }
      }
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      await loadCommerceConfig();

      const result = await getSemestralParkingService();

      if (isMounted.current) {
        if (result.success) {
          setRecords(result.data);
          setFilteredRecords(result.data);
          setStats({
            total: result.total,
            uniquePlates: result.uniquePlates,
            activeCount: result.activeCount,
            completedCount: result.completedCount,
            vehiclesInside: result.vehiclesInside || 0,
            vehiclesOutside: result.vehiclesOutside || 0,
          });
          hasLoaded.current = true;
        } else {
          setError(result.error || "Error al cargar los registros");
        }
      }
    } catch (err) {
      console.error("❌ Error en loadRecords:", err);
      if (isMounted.current) {
        setError(err.message || "Error al cargar los registros");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      isLoadingRef.current = false;
      setLoadAttempted(true);
    }
  }, [loadCommerceConfig, hasPermission, commerces]);

  // ============================================================
  // 3. CAMBIAR COMERCIO (después de loadRecords)
  // ============================================================

  const changeCommerce = useCallback(
    async (commerceId, commerceName) => {
      const result = switchCommerce(commerceId, commerceName);
      if (result.success) {
        setCurrentCommerceId(commerceId);
        setCurrentCommerceName(commerceName);
        // Recargar datos con el nuevo comercio
        setLoadAttempted(false);
        hasLoaded.current = false;
        await loadRecords();
        return true;
      }
      return false;
    },
    [loadRecords]
  );

  // ============================================================
  // 4. INICIALIZACIÓN
  // ============================================================

  useEffect(() => {
    if (initialized) return;

    const init = async () => {
      try {
        // Obtener usuario actual
        const user = getCurrentUser();

        // Obtener permisos del usuario
        const commercesResult = await getUserCommerces();

        if (commercesResult.success) {
          // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "registros"
          const filteredCommerces = filterCommercesByModulePermission(
            commercesResult.commerces,
            "registros"
          );
          setCommerces(filteredCommerces);

          // Si solo hay un comercio, seleccionarlo automáticamente
          if (filteredCommerces.length === 1) {
            const commerce = filteredCommerces[0];
            switchCommerce(commerce.id, commerce.name);
            setCurrentCommerceId(commerce.id);
            setCurrentCommerceName(commerce.name);
          } else if (filteredCommerces.length > 1) {
            // Si hay varios, usar el que ya está guardado o el primero
            const existingId = getCommerceId();
            const existingName = getCommerceName();

            if (existingId && filteredCommerces.some((c) => c.id === existingId)) {
              setCurrentCommerceId(existingId);
              setCurrentCommerceName(existingName || "");
            } else {
              const commerce = filteredCommerces[0];
              switchCommerce(commerce.id, commerce.name);
              setCurrentCommerceId(commerce.id);
              setCurrentCommerceName(commerce.name);
            }
          } else {
            // No hay comercios con permiso "ver" en registros
            setError("No tiene permisos para ver registros en ninguna propiedad");
            setHasPermission(false);
            setInitialized(true);
            return;
          }
        }

        // Verificar permisos para módulo registros
        const canView = hasModulePermission("registros", "ver");

        if (!canView) {
          setError("No tiene permisos para ver registros");
          setHasPermission(false);
          setInitialized(true);
          return;
        }

        setHasPermission(true);

        // Obtener permisos específicos para registros
        const permisos = getUserPermissionsForModule("registros");

        if (permisos.length > 0) {
          const rol = permisos[0].rol || {};
          setPermisosRegistros({
            ver: rol.ver || false,
            entrada: rol.entrada || false,
            salida: rol.salida || false,
          });
        }

        setInitialized(true);
      } catch (error) {
        console.error("❌ Error en inicialización:", error);
        setError("Error al inicializar el módulo");
        setInitialized(true);
      }
    };

    init();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // ============================================================
  // 5. CARGAR DATOS DESPUÉS DE LA INICIALIZACIÓN
  // ============================================================

  useEffect(() => {
    if (initialized && !hasLoaded.current && !loadAttempted) {
      loadRecords();
    }
  }, [initialized, loadRecords, loadAttempted]);

  // Cargar comercios después de la inicialización
  useEffect(() => {
    if (initialized && commerces.length === 0) {
      loadCommerces();
    }
  }, [initialized, loadCommerces, commerces.length]);

  // ============================================================
  // 6. OTRAS FUNCIONES
  // ============================================================

  const filterByPlate = useCallback(
    (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === "") {
        setFilteredRecords(records);
        return;
      }
      const query = searchTerm.trim().toUpperCase();
      const filtered = records.filter((record) => {
        if (!record.plate) return false;
        return record.plate.toUpperCase().includes(query);
      });
      setFilteredRecords(filtered);
    },
    [records]
  );

  const createCycle = useCallback(
    async (plate, date, observation) => {
      if (!permisosRegistros.entrada) {
        return { success: false, error: "No tiene permisos para crear ciclos de entrada" };
      }

      try {
        if (!terminales.entrada) {
          return { success: false, error: "No se encontró terminal de entrada configurado" };
        }

        const idterminal = terminales.entrada.idterminal;
        const result = await createCycleService(plate, idterminal, date, observation);

        if (result.success) {
          await loadRecords();
          return { success: true, message: result.message };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        console.error("❌ Error en createCycle:", error);
        return { success: false, error: error.message };
      }
    },
    [loadRecords, terminales, permisosRegistros]
  );

  const closeCycle = useCallback(
    async (id, plate, exitDate, observation) => {
      if (!permisosRegistros.salida) {
        return { success: false, error: "No tiene permisos para cerrar ciclos de salida" };
      }

      try {
        if (!terminales.salida) {
          return { success: false, error: "No se encontró terminal de salida configurado" };
        }

        const idterminal = terminales.salida.idterminal;
        const result = await closeCycleService(id, plate, idterminal, exitDate, observation);

        if (result.success) {
          await loadRecords();
          return { success: true, message: result.message };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        console.error("❌ Error en closeCycle:", error);
        return { success: false, error: error.message };
      }
    },
    [loadRecords, terminales, permisosRegistros]
  );

  const refreshData = useCallback(async () => {
    setLoadAttempted(false);
    hasLoaded.current = false;
    await loadRecords();
  }, [loadRecords]);

  // ============================================================
  // 7. RETORNAR
  // ============================================================

  return {
    records,
    filteredRecords,
    loading,
    error,
    stats,
    commerceConfig,
    terminales,
    commerces,
    currentCommerceId,
    currentCommerceName,
    hasPermission,
    permisosRegistros,
    filterByPlate,
    refreshData,
    closeCycle,
    createCycle,
    changeCommerce,
    hasData: filteredRecords.length > 0,
  };
};
